import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  AccountBalance,
  CURRENCY_SELECT_OPTIONS,
  FieldData,
  Select,
  SelectFilterEvent,
  SupportedTransferTypes,
  TextArea,
  TextField,
  formattedCurrency,
  getCurrencySign,
  sanitizeToNumeric,
  useForm,
} from 'vopay';
import useFetchPartnerAccounts, {
  ACCOUNT_PICKER_PAGE_SIZE,
} from '@/components/Entities/hooks/useApi/useFetchPartnerAccounts';
import type { PartnerAccountTransferParams } from '@/components/Entities/hooks/useApi/usePartnerAccountTransfer';
import { useToast } from '@/providers/Toast.provider';

export type TransferFormValues = {
  TransferTypeID: SupportedTransferTypes | '';
  /** A picker key (`<AccountID>-<Currency>`): an account is listed once per currency it holds, so the id alone is not unique. */
  DebitorAccountID: string;
  RecipientAccountID: string;
  /** Follows the From account; shown disabled so the review step still lists it. */
  Currency: string;
  Amount: string;
  ClientReferenceNumber: string;
  Notes: string;
};

const INITIAL_VALUES: TransferFormValues = {
  TransferTypeID: '',
  DebitorAccountID: '',
  RecipientAccountID: '',
  Currency: '',
  Amount: '',
  ClientReferenceNumber: '',
  Notes: '',
};

/** Typing in a picker refetches from the server after this pause. */
const SEARCH_DEBOUNCE_MS = 500;

/** Funding covers the debit, so these types are not bounded by the From account's balance. */
const FUNDING_TRANSFER_TYPES: ReadonlySet<SupportedTransferTypes> = new Set([
  'FundAndTransfer',
  'FundTransferAndWithdraw',
]);

const NO_ACCOUNTS: AccountBalance[] = [];

/** The typed amount as a number; the currency preset formats it with thousands separators. */
function toAmount(value: string) {
  return Number(sanitizeToNumeric(value));
}

export function toPickerKey(account: AccountBalance) {
  return `${account.AccountID}-${account.Currency}`;
}

/**
 * One row per recipient account: the row in the transfer's currency when the account holds one,
 * otherwise its first. The To picker is keyed by account id, so the balance it shows has to be
 * the one the money lands in.
 */
function recipientRows(rows: AccountBalance[], currency: string) {
  const byAccount = new Map<string, AccountBalance>();

  rows.forEach((row) => {
    const current = byAccount.get(row.AccountID);

    if (!current || (row.Currency === currency && current.Currency !== currency)) byAccount.set(row.AccountID, row);
  });

  return [...byAccount.values()];
}

type UseTransferFormConfigProps = {
  onSubmit: (values: PartnerAccountTransferParams, formValues: TransferFormValues) => Promise<void>;
  isReviewing: boolean;
  /** Fetches the pickers' accounts only while true, so a closed drawer costs nothing. */
  isActive: boolean;
  /** Preselects the From account (a row's Transfer button); absent = start blank. */
  initialFromAccount?: AccountBalance;
};

/**
 * The transfer form: type, the two account pickers, currency, amount, reference and notes. Each
 * picker searches the partner's active accounts on the server, so the options on screen are only
 * ever the latest results — every account seen is kept so a selection survives a later search and
 * the validation can still read its currency and balance.
 */
export default function useTransferFormConfig({
  onSubmit,
  isReviewing,
  isActive,
  initialFromAccount,
}: UseTransferFormConfigProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'PartnerAccountTransfer' });
  const toast = useToast();
  // One load seeds both pickers; each then searches on its own.
  const initialAccounts = useFetchPartnerAccounts();
  const fromAccounts = useFetchPartnerAccounts();
  const toAccounts = useFetchPartnerAccounts();

  const knownAccounts = useRef<Map<string, AccountBalance>>(
    new Map(initialFromAccount ? [[toPickerKey(initialFromAccount), initialFromAccount]] : []),
  );

  function getFromAccount(values: TransferFormValues) {
    return knownAccounts.current.get(values.DebitorAccountID);
  }

  function getToAccount(values: TransferFormValues) {
    return recipientRows([...knownAccounts.current.values()], values.Currency).find(
      (account) => account.AccountID === values.RecipientAccountID,
    );
  }

  async function search(picker: typeof fromAccounts, searchText: string) {
    try {
      const { balances } = await picker.fetchPartnerAccounts({
        SearchText: searchText,
        VopayAccountStatus: 'Active',
        Limit: ACCOUNT_PICKER_PAGE_SIZE,
      });

      balances.forEach((account) => knownAccounts.current.set(toPickerKey(account), account));
    } catch (error) {
      // The hook has logged the cause. The picker keeps whatever options it already had.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('loadAccountsError', { defaultValue: 'Could not load the accounts: {{errorMessage}}', errorMessage }),
      );
    }
  }

  useEffect(() => {
    if (isActive) search(initialAccounts, '');
  }, [isActive]);

  /**
   * Reloads the pickers after a transfer: the balances just moved, and the labels and the amount
   * check read them from the accounts seen so far, which the reload overwrites.
   */
  async function refreshAccounts() {
    fromAccounts.reset();
    toAccounts.reset();
    await search(initialAccounts, '');
  }

  const initialValues = useMemo(
    () => ({
      ...INITIAL_VALUES,
      ...(initialFromAccount && {
        DebitorAccountID: toPickerKey(initialFromAccount),
        Currency: initialFromAccount.Currency,
      }),
    }),
    [initialFromAccount],
  );

  // A stable empty list until the first results land, so the config is not rebuilt every render.
  const initialBalances = initialAccounts.response?.balances ?? NO_ACCOUNTS;
  const fromBalances = fromAccounts.response?.balances ?? initialBalances;
  const toBalances = toAccounts.response?.balances ?? initialBalances;
  const fetchingFromAccounts = fromAccounts.fetching || initialAccounts.fetching;
  const fetchingToAccounts = toAccounts.fetching || initialAccounts.fetching;

  const config = useMemo<FieldData<TransferFormValues>[]>(() => {
    // The raw type is what the request sends; only the label is localized.
    const transferTypeLabels: Record<SupportedTransferTypes, string> = {
      Transfer: t('transferTypeTransfer', { defaultValue: 'Transfer' }),
      FundAndTransfer: t('transferTypeFundAndTransfer', { defaultValue: 'Fund and Transfer' }),
      TransferAndWithdraw: t('transferTypeTransferAndWithdraw', { defaultValue: 'Transfer and Withdraw' }),
      FundTransferAndWithdraw: t('transferTypeFundTransferAndWithdraw', {
        defaultValue: 'Fund, Transfer and Withdraw',
      }),
    };

    const transferTypeOptions = Object.entries(transferTypeLabels).map(([value, label]) => ({ value, label }));

    // The From account's currency is always offered, so a currency outside the usual two still shows.
    const currencyOptions = (values: TransferFormValues) =>
      [...new Set([...Object.keys(CURRENCY_SELECT_OPTIONS), values.Currency])]
        .filter(Boolean)
        .map((currency) => ({ value: currency, label: currency }));

    const balanceLabel = (account: AccountBalance) =>
      `${account.Currency} ${getCurrencySign(account.Currency)}${formattedCurrency(account.AvailableBalance)}`;

    const fromLabel = (account: AccountBalance) =>
      t('accountFormattedBalance', {
        accountName: account.AccountName,
        balance: balanceLabel(account),
        defaultValue: '{{accountName}} | Available Balance: {{balance}}',
      });

    const toLabel = (account: AccountBalance) =>
      t('accountFormattedBalanceShort', {
        accountName: account.AccountName,
        balance: balanceLabel(account),
        defaultValue: '{{accountName}} | Balance: {{balance}}',
      });

    // The latest results, with the current selection kept at the top so a search that no longer
    // matches it cannot blank the picker.
    const fromOptions = (values: TransferFormValues) => {
      const selected = getFromAccount(values);

      return [
        ...(selected ? [selected] : []),
        ...fromBalances.filter((account) => !selected || toPickerKey(account) !== toPickerKey(selected)),
      ];
    };

    const toOptions = (values: TransferFormValues) => {
      const selected = getToAccount(values);

      return [
        ...(selected ? [selected] : []),
        ...recipientRows(toBalances, values.Currency).filter(
          (account) => !selected || account.AccountID !== selected.AccountID,
        ),
      ];
    };

    const searchOnFilter = (picker: typeof fromAccounts) => (event: SelectFilterEvent<AccountBalance>) => {
      // The server does the matching; the picker's own filtering would hide results it has not seen.
      event.preventDefault();
      event.debounce(() => search(picker, event.filter), SEARCH_DEBOUNCE_MS);
    };

    return [
      {
        fieldName: 'TransferTypeID',
        label: () => t('transferTypeIDLabel', { defaultValue: 'Transfer Type' }),
        getInputState: () => 'required',
        showField: () => true,
        validate: () =>
          Yup.string()
            .label(t('transferTypeIDLabel', { defaultValue: 'Transfer Type' }))
            .required(),
        renderField: ({ getFormikData }) => (
          <Select
            version="2"
            id="transferType"
            {...getFormikData<TransferFormValues['TransferTypeID']>({})}
            placeholder={t('transferTypeIDPlaceholder', { defaultValue: 'Select a Transfer Type' })}
            options={transferTypeOptions}
            sort={false}
          />
        ),
      },
      {
        fieldName: 'DebitorAccountID',
        label: () => t('debitorAccountIDLabel', { defaultValue: 'Debitor Account (From)' }),
        getInputState: () => 'required',
        showField: () => true,
        serializeField: (values) => getFromAccount(values)?.AccountID ?? '',
        validate: () =>
          Yup.string()
            .label(t('debitorAccountIDLabel', { defaultValue: 'Debitor Account (From)' }))
            .required(),
        renderField: ({ ctx, getFormikData }) => (
          <Select
            version="2"
            id="transferFrom"
            {...getFormikData<TransferFormValues['DebitorAccountID'], undefined, { data: AccountBalance | null }>({
              // The transfer is in the debitor's currency, so the Currency field follows this one.
              onChange: (_event, option) => ctx.setFieldValue('Currency', option?.data?.Currency ?? ''),
            })}
            placeholder={t('debitorAccountIDPlaceholder', { defaultValue: 'Select a Creditor Account' })}
            options={fromOptions(ctx.values)}
            optionValue={toPickerKey}
            optionLabel={fromLabel}
            loading={fetchingFromAccounts}
            filterable
            onFilter={searchOnFilter(fromAccounts)}
            emptyFilterMsg={t('noAccountsFound', { defaultValue: 'No accounts found' })}
            sort={false}
          />
        ),
      },
      {
        fieldName: 'RecipientAccountID',
        label: () => t('recipientAccountIDLabel', { defaultValue: 'Recipient Account (To)' }),
        getInputState: () => 'required',
        showField: () => true,
        validate: (ctx) =>
          Yup.string()
            .label(t('recipientAccountIDLabel', { defaultValue: 'Recipient Account (To)' }))
            .required()
            .test(
              'not-the-debitor',
              t('sameAccountValidation', {
                defaultValue: 'Invalid account. Debitor and Recipient cannot be the same.',
              }),
              (value) => !value || value !== getFromAccount(ctx.values)?.AccountID,
            ),
        renderField: ({ ctx, getFormikData }) => (
          <Select
            version="2"
            id="transferTo"
            {...getFormikData<TransferFormValues['RecipientAccountID']>({})}
            placeholder={t('recipientAccountIDPlaceholder', { defaultValue: 'Select a Receiver Account' })}
            options={toOptions(ctx.values)}
            optionValue={(account) => account.AccountID}
            optionLabel={toLabel}
            loading={fetchingToAccounts}
            filterable
            onFilter={searchOnFilter(toAccounts)}
            emptyFilterMsg={t('noAccountsFound', { defaultValue: 'No accounts found' })}
            sort={false}
          />
        ),
      },
      {
        fieldName: 'Currency',
        label: () => t('currencyLabel', { defaultValue: 'Currency' }),
        getInputState: () => 'required',
        showField: () => true,
        validate: () =>
          Yup.string()
            .label(t('currencyLabel', { defaultValue: 'Currency' }))
            .required(),
        renderField: ({ ctx, getFormikData }) => (
          // The Accounts page's filter bar has a `Currency` select of its own; the ids keep the four
          // pickers' test ids distinct from it.
          <Select
            version="2"
            id="transferCurrency"
            {...getFormikData<TransferFormValues['Currency']>({})}
            placeholder={t('currencyPlaceholder', { defaultValue: 'Select a Currency' })}
            options={currencyOptions(ctx.values)}
            // Disabled on the input only: a disabled field state would also drop it from validation
            // and from the request.
            disabled
            sort={false}
          />
        ),
      },
      {
        fieldName: 'Amount',
        label: () => t('amountLabel', { defaultValue: 'Amount' }),
        getInputState: () => 'required',
        showField: () => true,
        // The currency preset keeps the thousands separators in the value; the request wants digits.
        serializeField: (values) => sanitizeToNumeric(values.Amount),
        validate: (ctx) =>
          Yup.string()
            .label(t('amountLabel', { defaultValue: 'Amount' }))
            .required()
            .test(
              'positive',
              t('invalidAmountValidation', { defaultValue: 'Invalid amount. Please enter a valid amount.' }),
              (value) => !value || toAmount(value) > 0,
            )
            .test(
              'within-the-debitor-balance',
              t('amountLessThanBalanceValidation', {
                defaultValue:
                  'Transfer amount is too high. Please input a transfer amount below the max debitor balance',
              }),
              (value) => {
                const { TransferTypeID } = ctx.values;
                const fromAccount = getFromAccount(ctx.values);

                if (!value || !fromAccount || (TransferTypeID && FUNDING_TRANSFER_TYPES.has(TransferTypeID))) {
                  return true;
                }

                return toAmount(value) <= Number(fromAccount.AvailableBalance);
              },
            ),
        renderField: ({ getFormikData }) => (
          <TextField
            {...getFormikData<TransferFormValues['Amount']>({})}
            preset="currency"
            variant="outlineShadow"
            placeholder={t('amountPlaceholder', { defaultValue: '$0.00' })}
          />
        ),
      },
      {
        fieldName: 'ClientReferenceNumber',
        label: () => t('clientReferenceNumberLabel', { defaultValue: 'Client Reference Number' }),
        getInputState: () => 'optional',
        showField: () => true,
        renderField: ({ getFormikData }) => (
          <TextField
            {...getFormikData<TransferFormValues['ClientReferenceNumber']>({})}
            variant="outlineShadow"
            placeholder={t('clientReferenceNumberPlaceholder', { defaultValue: 'Enter Client Reference Number' })}
          />
        ),
      },
      {
        fieldName: 'Notes',
        label: () => t('notesLabel', { defaultValue: 'Notes' }),
        getInputState: () => 'optional',
        showField: () => true,
        renderField: ({ getFormikData }) => (
          <TextArea
            {...getFormikData<TransferFormValues['Notes']>({})}
            variant="outlineShadow"
            placeholder={t('notesPlaceholder', { defaultValue: 'Enter Notes' })}
            maxLength={5000}
            rows={3}
          />
        ),
      },
    ];
  }, [t, fromBalances, toBalances, fetchingFromAccounts, fetchingToAccounts]);

  const { ctx, renderFormElements } = useForm<TransferFormValues, PartnerAccountTransferParams>({
    initialValues,
    config,
    onSubmit: (values, formCtx) => onSubmit(values, formCtx.values),
    isReviewing,
  });

  // A subset renders only those fields; the whole config still drives validation and serialization.
  const renderFields = (fieldNames?: (keyof TransferFormValues)[]) =>
    renderFormElements({
      config: fieldNames ? config.filter((field) => fieldNames.includes(field.fieldName)) : config,
    });

  return { ctx, renderFormElements: renderFields, getFromAccount, getToAccount, refreshAccounts };
}
